import { useState, useMemo, useEffect } from 'react';
import { Search, MailCheck, ClipboardList, Flag, Mail, Loader2, Download } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from '../payment-recon/SortableTh';
import { CommentBadge } from '../payment-recon/CommentBadge';
import { SendReminderModal } from './SendReminderModal';
import { RiskBadge } from './RiskBadge';
import { formatMoney, formatDate, agingClass, bucketLabel, levelBadgeClass, todayIso, riskSortValue } from '../../utils/arDunningFormat';
import type { ArOpenItem, ArCompanyOption, ArCustomerRisk } from '../../types/arDunning';

interface Props {
    items: ArOpenItem[];
    loading: boolean;
    canEdit: boolean;
    isConsolidated: boolean;
    /** Page-level selected company ('' = all) — drives the Excel export scope. */
    company: string;
    companies: ArCompanyOption[];
    showSellerFilter: boolean;
    /** Per-customer risk lookup for the row badges + sort/filter. */
    riskByCustomer: Map<string, ArCustomerRisk>;
    /** Drill-down preset from Overview/Aging/KPIs; applied when presetNonce changes. */
    preset?: { bucket?: string; q?: string; flag?: '' | 'disputed' | 'promise' } | null;
    presetNonce?: number;
    onOpenDetail: (item: ArOpenItem) => void;
    onOpenAction: (item: ArOpenItem) => void;
    onChanged: () => void;
}

const BUCKET_FILTERS = [
    { value: '', label: 'All ages' },
    { value: 'overdue', label: 'Overdue only' },
    { value: '1-30', label: '1–30' },
    { value: '31-60', label: '31–60' },
    { value: '61-90', label: '61–90' },
    { value: '91-180', label: '91–180' },
    { value: '181-365', label: '181–365' },
    { value: '365+', label: '365+' },
    { value: 'current', label: 'Current' },
    { value: 'unknown', label: 'No due date' },
];

const RENDER_CAP = 500;

const CATEGORY_FILTERS = [
    { value: '', label: 'All categories' },
    { value: 'Standard', label: 'Standard' },
    { value: 'RibaIssued', label: 'RiBa (SBF)' },
    { value: 'Dishonored', label: 'Insoluti' },
];

/** Rows are keyed by ItemKey + CustomerID: a batch insoluto (dishonored RiBa) can share an
 *  ItemKey across two customers, so ItemKey alone is not unique for React keys / selection. */
const rowKey = (i: ArOpenItem) => `${i.ItemKey}::${i.CustomerID}`;

export function OpenItemsTab({ items, loading, canEdit, isConsolidated, company, companies, showSellerFilter, riskByCustomer, preset, presetNonce, onOpenDetail, onOpenAction, onChanged }: Props) {
    const { toast } = useToast();
    const [bucket, setBucket] = useState('');
    const [seller, setSeller] = useState('');
    const [flag, setFlag] = useState<'' | 'disputed' | 'promise'>('');
    const [risk, setRisk] = useState<'' | 'red' | 'amber'>('');
    const [category, setCategory] = useState('');
    const [q, setQ] = useState('');
    const [exporting, setExporting] = useState(false);

    // The category facet is dit-only (issued RiBa / insoluti). Hide it entirely when the loaded
    // set is all 'Standard' so other companies see an unchanged filter row.
    const hasCategories = useMemo(() => items.some(i => i.ItemCategory !== 'Standard'), [items]);

    // Apply a drill-down preset from another tab (Overview/Aging/KPI tile).
    useEffect(() => {
        if (presetNonce === undefined || !preset) return;
        setBucket(preset.bucket ?? '');
        setQ(preset.q ?? '');
        setFlag(preset.flag ?? '');
        setSeller('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetNonce]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkLevel, setBulkLevel] = useState(1);
    const [bulkDate, setBulkDate] = useState(todayIso());
    const [busy, setBusy] = useState(false);
    const [sendMode, setSendMode] = useState<'per-customer' | 'combined' | null>(null);

    // Sales-manager options are derived from the currently-loaded items, which are already
    // scoped to the selected company. This pre-filters the picker to that company's sellers
    // and dedupes the per-company SalesManagerIDs that share a name.
    const sellerOptions = useMemo(() => {
        const byId = new Map<string, string>();
        for (const i of items) {
            if (i.SalesManagerID) byId.set(i.SalesManagerID, i.SalesManagerName ?? i.SalesManagerID);
        }
        return Array.from(byId, ([SalesManagerID, SalesManagerName]) => ({ SalesManagerID, SalesManagerName }))
            .sort((a, b) => a.SalesManagerName.localeCompare(b.SalesManagerName));
    }, [items]);

    // Clear a stale seller selection when it's no longer in the (company-scoped) option set.
    useEffect(() => {
        if (seller && !sellerOptions.some(s => s.SalesManagerID === seller)) setSeller('');
    }, [sellerOptions, seller]);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        return items.filter(i => {
            if (bucket === 'overdue') { if (i.AgingBucket === 'current' || i.AgingBucket === 'unknown') return false; }
            else if (bucket && i.AgingBucket !== bucket) return false;
            if (category && i.ItemCategory !== category) return false;
            if (seller && i.SalesManagerID !== seller) return false;
            if (flag === 'disputed' && !i.IsDisputed) return false;
            if (flag === 'promise' && !i.HasOpenPromise) return false;
            if (risk) {
                const lvl = riskByCustomer.get(i.CustomerID)?.RiskLevel;
                if (risk === 'red' && lvl !== 'red') return false;
                if (risk === 'amber' && lvl !== 'red' && lvl !== 'amber') return false;
            }
            if (query && !(`${i.InvoiceNo} ${i.CustomerName ?? ''} ${i.CustomerCode ?? ''}`.toLowerCase().includes(query))) return false;
            return true;
        });
    }, [items, bucket, category, seller, flag, risk, riskByCustomer, q]);

    const { sorted, sort, toggleSort } = useTableSort(filtered, {
        invoice: r => r.InvoiceNo,
        company: r => r.CompanyCode,
        customer: r => r.CustomerName,
        due: r => r.DueDate,
        open: r => r.OpenAmountEUR,
        aging: r => r.DaysOverdue,
        level: r => r.LastReminderLevel ?? 0,
        next: r => r.NextLevel,
        risk: r => { const rk = riskByCustomer.get(r.CustomerID); return rk ? riskSortValue(rk.RiskLevel, rk.RiskScore) : 0; },
    }, { key: 'aging', dir: 'desc' });

    const shown = sorted.slice(0, RENDER_CAP);
    const subtotalEur = useMemo(() => filtered.reduce((s, i) => s + (i.OpenAmountEUR || 0), 0), [filtered]);

    const toggle = (key: string) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const allShownSelected = shown.length > 0 && shown.every(i => selected.has(rowKey(i)));
    const toggleAll = () => setSelected(prev => {
        if (allShownSelected) { const next = new Set(prev); shown.forEach(i => next.delete(rowKey(i))); return next; }
        const next = new Set(prev); shown.forEach(i => next.add(rowKey(i))); return next;
    });

    const selectedItems = useMemo(() => filtered.filter(i => selected.has(rowKey(i))), [filtered, selected]);
    // Issued RiBa is collected by the bank (SBF) and is never dunned — exclude it from the
    // reminder flows (mark-reminded + send). Kept selectable so the user still sees it counted.
    const remindableItems = useMemo(() => selectedItems.filter(i => i.ItemCategory !== 'RibaIssued'), [selectedItems]);
    const ribaExcludedCount = selectedItems.length - remindableItems.length;

    const runBulk = async (targets: ArOpenItem[], make: (i: ArOpenItem) => Promise<unknown>, label: string) => {
        if (targets.length === 0) return;
        setBusy(true);
        try {
            const results = await Promise.allSettled(targets.map(make));
            const ok = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.length - ok;
            toast(`${label}: ${ok} done${failed ? `, ${failed} failed` : ''}`, failed ? 'info' : 'success');
            setSelected(new Set());
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Bulk action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    // "Mark reminded" excludes issued RiBa (never dunned).
    const bulkRemind = () => runBulk(remindableItems, i => arDunningApi.createAction({ entityType: 'Invoice', entityRef: i.ItemKey, actionType: 'ReminderSent', dunningLevel: bulkLevel }), `Marked reminded L${bulkLevel}`);
    const bulkFollowUp = () => runBulk(selectedItems, i => arDunningApi.createAction({ entityType: 'Invoice', entityRef: i.ItemKey, actionType: 'FollowUp', dueAt: bulkDate }), 'Follow-up added');
    const bulkDispute = () => runBulk(selectedItems, i => arDunningApi.createAction({ entityType: 'Invoice', entityRef: i.ItemKey, actionType: 'Dispute' }), 'Flagged disputed');

    const amount = (i: ArOpenItem) => isConsolidated ? formatMoney(i.OpenAmountEUR) : formatMoney(i.OpenAmountFCY, i.Currency);

    const runExport = async () => {
        setExporting(true);
        try {
            await arDunningApi.downloadOpenItemsExport({
                companyCode: company || undefined,
                salesManagerId: seller || undefined,
                agingBucket: bucket && bucket !== 'overdue' ? bucket : undefined,
                overdueOnly: bucket === 'overdue',
                q: q.trim() || undefined,
            });
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Export failed', 'error');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <select value={bucket} onChange={e => setBucket(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                    {BUCKET_FILTERS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
                {showSellerFilter && (
                    <select value={seller} onChange={e => setSeller(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                        <option value="">All sales managers</option>
                        {sellerOptions.map(s => <option key={s.SalesManagerID} value={s.SalesManagerID}>{s.SalesManagerName}</option>)}
                    </select>
                )}
                <select value={flag} onChange={e => setFlag(e.target.value as '' | 'disputed' | 'promise')} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                    <option value="">All items</option>
                    <option value="promise">With promise</option>
                    <option value="disputed">Disputed / hold</option>
                </select>
                <select value={risk} onChange={e => setRisk(e.target.value as '' | 'red' | 'amber')} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" title="Filter by customer payment risk">
                    <option value="">Any risk</option>
                    <option value="red">High risk</option>
                    <option value="amber">Watch +</option>
                </select>
                {hasCategories && (
                    <select value={category} onChange={e => setCategory(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" title="Filter by RiBa category">
                        {CATEGORY_FILTERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                )}
                <div className="relative min-w-[200px] flex-1">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                    <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invoice no / customer…" className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm" />
                </div>
                <button onClick={runExport} disabled={exporting || filtered.length === 0} title="Export the filtered list to Excel" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
                </button>
            </div>

            {/* Bulk action bar */}
            {canEdit && selected.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-sm">
                    <span className="font-medium text-indigo-800">{selected.size} selected</span>
                    {ribaExcludedCount > 0 && (
                        <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700" title="Issued RiBa is collected by the bank (SBF) and is excluded from reminders">
                            {ribaExcludedCount} RiBa excluded from reminders
                        </span>
                    )}
                    <span className="mx-1 h-4 w-px bg-indigo-200" />
                    <select value={bulkLevel} onChange={e => setBulkLevel(Number(e.target.value))} className="rounded border border-slate-300 px-1.5 py-1 text-sm">
                        {[1, 2, 3].map(l => <option key={l} value={l}>L{l}</option>)}
                    </select>
                    <button onClick={bulkRemind} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"><MailCheck className="h-3.5 w-3.5" /> Mark reminded</button>
                    <span className="mx-1 h-4 w-px bg-indigo-200" />
                    <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} className="rounded border border-slate-300 px-1.5 py-1 text-sm" />
                    <button onClick={bulkFollowUp} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><ClipboardList className="h-3.5 w-3.5" /> Follow-up</button>
                    <button onClick={bulkDispute} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"><Flag className="h-3.5 w-3.5" /> Dispute</button>
                    <span className="mx-1 h-4 w-px bg-indigo-200" />
                    <button onClick={() => setSendMode('per-customer')} disabled={busy || remindableItems.length === 0} title="Compose &amp; send dunning e-mails (one per customer)" className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"><Mail className="h-3.5 w-3.5" /> Send reminder</button>
                    <button onClick={() => setSendMode('combined')} disabled={busy || remindableItems.length === 0} title="One internal e-mail with all selected items, grouped by customer (e.g. to the responsible sales manager)" className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"><Mail className="h-3.5 w-3.5" /> Internal e-mail</button>
                    {busy && <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />}
                </div>
            )}

            {/* Result summary */}
            <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                <span>{filtered.length} item{filtered.length === 1 ? '' : 's'} · filtered subtotal {formatMoney(subtotalEur)}</span>
                {filtered.length > RENDER_CAP && <span className="text-amber-600">Showing first {RENDER_CAP} — refine filters to narrow</span>}
            </div>

            {loading ? (
                <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : shown.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No open items in this view.</div>
            ) : (
                <div className="max-h-[62vh] overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                            <tr>
                                {canEdit && <th className="w-8 px-3 py-2"><input type="checkbox" checked={allShownSelected} onChange={toggleAll} /></th>}
                                <SortableTh label="Invoice" sortKey="invoice" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Co." sortKey="company" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Risk" sortKey="risk" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Due" sortKey="due" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Open" sortKey="open" sort={sort} onSort={toggleSort} className="text-right" />
                                <SortableTh label="Aging" sortKey="aging" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Level" sortKey="level" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Next" sortKey="next" sort={sort} onSort={toggleSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {shown.map(i => (
                                <tr
                                    key={rowKey(i)}
                                    onClick={() => onOpenDetail(i)}
                                    onContextMenu={(e) => { e.preventDefault(); onOpenAction(i); }}
                                    className="cursor-pointer hover:bg-slate-50"
                                >
                                    {canEdit && (
                                        <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected.has(rowKey(i))} onChange={() => toggle(rowKey(i))} />
                                        </td>
                                    )}
                                    <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">
                                        <span className="inline-flex items-center gap-1.5">
                                            {i.InvoiceNo}
                                            {i.DocumentType === 'CreditMemo' && <span className="rounded bg-emerald-50 px-1 text-xs text-emerald-700">CM</span>}
                                            {i.ItemCategory === 'RibaIssued' && <span title="Issued RiBa — collected by the bank (SBF)" className="rounded bg-blue-50 px-1 text-xs text-blue-700">RiBa</span>}
                                            {i.ItemCategory === 'Dishonored' && <span title="Dishonored RiBa (insoluto) — bank refused collection" className="rounded bg-rose-100 px-1 text-xs font-semibold text-rose-700">Insoluto</span>}
                                            {i.IsDisputed && <Flag className="h-3 w-3 text-amber-600" />}
                                            {i.HasOpenPromise && <span title="Open promise-to-pay" className="rounded bg-sky-50 px-1 text-xs text-sky-700">P</span>}
                                            <CommentBadge count={i.CommentCount} />
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs uppercase text-slate-500">{i.CompanyCode}</td>
                                    <td className="max-w-[16rem] truncate px-3 py-2 text-slate-600" title={i.CustomerName ?? ''}>{i.CustomerName ?? i.CustomerCode}</td>
                                    <td className="px-3 py-2"><RiskBadge risk={riskByCustomer.get(i.CustomerID)} /></td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(i.DueDate)}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-slate-800">{amount(i)}</td>
                                    <td className={`whitespace-nowrap px-3 py-2 text-xs ${agingClass(i.AgingBucket)}`}>{i.DaysOverdue > 0 ? `${i.DaysOverdue}d` : bucketLabel(i.AgingBucket)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${levelBadgeClass(i.LastReminderLevel ?? 0)}`}>{i.LastReminderLevel ? `L${i.LastReminderLevel}` : '–'}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {i.NextLevel > 0 ? <span className="text-xs font-semibold text-rose-700">L{i.NextLevel}</span> : <span className="text-xs text-slate-300">–</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="mt-2 text-xs text-slate-400">Left-click a row for details &amp; comments · right-click to record an action.</p>

            {sendMode && (
                <SendReminderModal
                    items={remindableItems}
                    excludedRibaCount={ribaExcludedCount}
                    initialLevel={bulkLevel}
                    mode={sendMode}
                    companies={companies}
                    canEdit={canEdit}
                    onClose={() => setSendMode(null)}
                    onSent={() => { setSelected(new Set()); onChanged(); }}
                />
            )}
        </div>
    );
}
